import {
  Pause,
  Play,
  Ban,
  RotateCcw,
  Workflow as WorkflowIcon,
  FolderGit2,
  Inbox,
  Bot,
  Network,
  FileText,
  Download,
  Check,
  X,
  ArrowUpRight,
  Zap,
  Shield,
  DollarSign,
  ScrollText,
} from "lucide-react";
import type { HiveExecution } from "@/types";
import { useExecution } from "@/store/executionStore";
import { useWorkflow } from "@/store/workflowStore";
import { useHive } from "@/store/useHive";
import { EXEC_STATUS_META, RiskBadge, Pill } from "@/components/hive/shared";
import { SampleBadge } from "@/components/ui/SampleBadge";

export function ExecutionDetailPanel({ exec }: { exec: HiveExecution }) {
  const pause = useExecution((s) => s.pauseExecution);
  const resume = useExecution((s) => s.resumeExecution);
  const cancel = useExecution((s) => s.cancelExecution);
  const retryStep = useExecution((s) => s.retryStep);
  const rerunFromStep = useExecution((s) => s.rerunFromStep);
  const exportLogs = useExecution((s) => s.exportLogs);
  const approvals = useExecution((s) => s.approvals);
  const openApprovalModal = useExecution((s) => s.openApprovalModal);

  const selectWorkflow = useWorkflow((s) => s.selectWorkflow);
  const openApp = useHive((s) => s.openApp);
  const pushToast = useHive((s) => s.pushToast);

  const m = EXEC_STATUS_META[exec.status];
  const relatedApproval = approvals.find((a) => a.executionId === exec.id && a.status === "pending");
  const failedStep = exec.steps.find((s) => s.status === "failed");

  const viewWorkflow = () => { selectWorkflow(exec.workflowId); openApp("workflows"); };

  return (
    <div className="flex h-full w-[332px] shrink-0 flex-col border-l border-app bg-surface/30">
      <div className="flex items-center gap-2.5 border-b border-app px-4 py-3">
        <div className="grid h-8 w-8 place-items-center rounded-lg text-white shadow" style={{ background: `linear-gradient(135deg, ${m.color}, ${m.color}aa)` }}>
          <Zap size={15} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-app">Execution Detail</div>
          <div className="truncate text-[11px] text-app-2">{exec.id}</div>
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto no-scrollbar p-3">
        {/* summary */}
        <Card>
          <div className="flex items-center gap-2">
            <Pill color={m.color} dot={exec.status === "running"} pulse={exec.status === "running"}>{m.label}</Pill>
            <RiskBadge risk={exec.risk} />
            <span className="ml-auto text-[11px] tabular-nums text-app-2">{Math.round(exec.progress * 100)}%</span>
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-xs font-medium text-app">
            {exec.name}
            {exec.isSample && <SampleBadge />}
          </div>
          <Grid>
            <KV k="Owner" v={exec.owner} />
            <KV k="Started" v={exec.startedAt} />
            <KV k="Duration" v={exec.duration} />
            <KV k="Current" v={exec.currentStep} />
          </Grid>
        </Card>

        {/* linked entities */}
        <Section title="Linked">
          <LinkRow icon={<WorkflowIcon size={13} />} label="Workflow" value={exec.workflowName} onClick={viewWorkflow} />
          <LinkRow icon={<Zap size={13} />} label="Trigger" value={exec.triggerEvent} />
          {exec.taskId && <LinkRow icon={<Inbox size={13} />} label="Task" value={exec.taskId} onClick={() => openApp("tasks")} />}
          {exec.workspace && <LinkRow icon={<FolderGit2 size={13} />} label="Workspace" value={exec.workspace} onClick={() => openApp("workspaces")} />}
          {exec.repo && <LinkRow icon={<FolderGit2 size={13} />} label="Repo" value={exec.repo} />}
          {exec.agent && <LinkRow icon={<Bot size={13} />} label="Agent" value={exec.agent} onClick={() => openApp("ares")} />}
        </Section>

        {/* cost + permissions */}
        <Section title="Cost & Permissions">
          <div className="mb-2 flex items-center justify-between rounded-lg border border-app bg-surface-2/40 px-2.5 py-2">
            <span className="flex items-center gap-1.5 text-[11px] text-app-1"><DollarSign size={12} /> Spend so far</span>
            <span className="text-sm font-semibold tabular-nums text-app">${exec.cost.toFixed(2)}</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {exec.permissionsUsed.map((p) => (
              <span key={p} className="rounded border border-app bg-surface-2/60 px-1.5 py-0.5 font-mono text-[9px] text-app-1">{p}</span>
            ))}
          </div>
        </Section>

        {/* artifacts */}
        {exec.artifacts.length > 0 && (
          <Section title="Artifacts">
            {exec.artifacts.map((a) => (
              <div key={a.name} className="flex items-center gap-2 rounded-lg border border-app bg-surface-2/40 px-2.5 py-1.5">
                <FileText size={12} className="text-app-2" />
                <span className="flex-1 truncate text-[11px] text-app-1">{a.name}</span>
                <span className="text-[10px] text-app-2">{a.size}</span>
              </div>
            ))}
          </Section>
        )}

        {/* logs */}
        <Section title="Logs" icon={<ScrollText size={12} />}>
          <div className="space-y-1">
            {exec.logs.slice(0, 5).map((l, i) => (
              <div key={i} className="flex items-start gap-1.5 text-[10px]">
                <span className="tabular-nums text-app-2">{l.time}</span>
                <span className={LOG_COLOR[l.level]}>{l.text}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* recovery */}
        <Section title="Recovery Actions" icon={<Shield size={12} />}>
          <div className="grid grid-cols-2 gap-1.5">
            <MiniAction icon={<FolderGit2 size={12} />} label="Open Workspace" onClick={() => openApp("workspaces")} />
            <MiniAction icon={<Inbox size={12} />} label="Open Task" onClick={() => openApp("tasks")} />
            <MiniAction icon={<WorkflowIcon size={12} />} label="View Workflow" onClick={viewWorkflow} />
            <MiniAction icon={<Network size={12} />} label="Open BYONC" onClick={() => openApp("byonc")} />
          </div>
        </Section>
      </div>

      {/* sticky action bar */}
      <div className="shrink-0 space-y-1.5 border-t border-app p-3">
        {relatedApproval ? (
          <div className="grid grid-cols-2 gap-1.5">
            <button onClick={() => openApprovalModal(relatedApproval.id)} className="flex items-center justify-center gap-1.5 rounded-lg bg-emerald-500/15 py-2 text-[11px] font-semibold text-emerald-500 transition hover:bg-emerald-500/25"><Check size={13} /> Approve</button>
            <button onClick={() => openApprovalModal(relatedApproval.id)} className="flex items-center justify-center gap-1.5 rounded-lg bg-rose-500/15 py-2 text-[11px] font-semibold text-rose-500 transition hover:bg-rose-500/25"><X size={13} /> Reject</button>
          </div>
        ) : null}
        <div className="grid grid-cols-3 gap-1.5">
          {exec.status === "running" || exec.status === "waiting" ? (
            <ActionBar icon={<Pause size={13} />} label="Pause" onClick={() => pause(exec.id)} />
          ) : (
            <ActionBar icon={<Play size={13} />} label="Resume" onClick={() => resume(exec.id)} />
          )}
          <ActionBar
            icon={<RotateCcw size={13} />}
            label="Retry"
            disabled={!failedStep}
            onClick={() => failedStep && retryStep(exec.id, failedStep.id)}
          />
          <ActionBar
            icon={<ArrowUpRight size={13} />}
            label="Rerun"
            onClick={() => rerunFromStep(exec.id, exec.steps[0]?.id)}
          />
          <ActionBar icon={<Ban size={13} />} label="Cancel" danger onClick={() => cancel(exec.id)} />
          <ActionBar icon={<Download size={13} />} label="Export" onClick={() => exportLogs(exec.id)} />
          <ActionBar icon={<Bot size={13} />} label="Open ARES" onClick={() => { openApp("ares"); pushToast({ title: "Opening ARES", body: exec.agent ?? "agent run", tone: "info" }); }} />
        </div>
      </div>
    </div>
  );
}

const LOG_COLOR: Record<string, string> = {
  info: "text-app-1",
  warn: "text-amber-500",
  error: "text-rose-500",
  success: "text-emerald-500",
};

function Card({ children }: { children: React.ReactNode }) {
  return <div className="rounded-xl border border-app bg-surface/40 p-3">{children}</div>;
}
function Section({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-app-2">{icon} {title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Grid({ children }: { children: React.ReactNode }) {
  return <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5">{children}</div>;
}
function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[9px] uppercase tracking-wider text-app-2">{k}</div>
      <div className="truncate text-[11px] font-medium text-app">{v}</div>
    </div>
  );
}
function LinkRow({ icon, label, value, onClick }: { icon: React.ReactNode; label: string; value: string; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`flex w-full items-center gap-2 rounded-lg border border-app bg-surface-2/40 px-2.5 py-1.5 text-left transition ${onClick ? "hover:border-app-strong" : "cursor-default"}`}
    >
      <span className="text-app-2">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[9px] uppercase tracking-wider text-app-2">{label}</div>
        <div className="truncate text-[11px] font-medium text-app">{value}</div>
      </div>
      {onClick && <ArrowUpRight size={12} className="text-app-2" />}
    </button>
  );
}
function MiniAction({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex items-center gap-1.5 rounded-lg border border-app bg-surface-2/50 px-2 py-1.5 text-[10px] font-medium text-app-1 transition hover:border-app-strong">
      {icon} {label}
    </button>
  );
}
function ActionBar({ icon, label, onClick, danger, disabled }: { icon: React.ReactNode; label: string; onClick: () => void; danger?: boolean; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 text-[10px] font-medium transition ${
        disabled ? "cursor-not-allowed border-app text-app-2 opacity-50" : danger ? "border-rose-500/30 bg-rose-500/10 text-rose-500 hover:bg-rose-500/20" : "border-app bg-surface-2/50 text-app-1 hover:border-app-strong"
      }`}
    >
      {icon} {label}
    </button>
  );
}
